import { useState, memo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CheckCircle2, AlertTriangle, XCircle, Clock, MapPin, Eye, ChevronDown, ChevronUp, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gcsGetPublicUrl } from '@/lib/gcsStorage'

import { getDisplayStatus } from '@/lib/qualificationStatus'

const statusConfig = {
  completed: { label: 'Valid', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expiring_soon: { label: 'Expiring Soon', icon: AlertTriangle, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  expired: { label: 'Expired', icon: XCircle, className: 'bg-red-50 text-red-700 border-red-200' },
  in_progress: { label: 'In Progress', icon: Clock, className: 'bg-blue-50 text-blue-700 border-blue-200' },
  booked: { label: 'Booked', icon: Clock, className: 'bg-blue-50 text-blue-700 border-blue-200' },
  quoting: { label: 'Quoting', icon: Clock, className: 'bg-purple-50 text-purple-700 border-purple-200' },
  quoted: { label: 'Quoted', icon: Clock, className: 'bg-purple-50 text-purple-700 border-purple-200' },
  pending: { label: 'Missing', icon: AlertTriangle, className: 'bg-orange-50 text-orange-700 border-orange-200' },
  not_started: { label: 'Missing', icon: AlertTriangle, className: 'bg-orange-50 text-orange-700 border-orange-200' },
  not_evidenced: { label: 'Not Evidenced', icon: AlertTriangle, className: 'bg-orange-50 text-orange-700 border-orange-200' },
}

function getDaysUntilExpiry(expiryDate) {
  if (!expiryDate) return null
  const expiry = new Date(expiryDate)
  const now = new Date()
  const diffMs = expiry - now
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

function formatExpiryText(days) {
  if (days === null) return null
  if (days < 0) return `Expired ${Math.abs(days)} days ago`
  if (days === 0) return 'Expires today'
  if (days === 1) return 'Expires tomorrow'
  if (days <= 30) return `${days} days remaining`
  return `${Math.floor(days / 30)} months remaining`
}

function formatRenewalPeriod(value) {
  if (!value || value === 'none' || value === 'None') return null
  const map = {
    '1_month': '1 month',
    '3_months': '3 months',
    '6_months': '6 months',
    '1_year': '1 year',
    '2_years': '2 years',
    '3_years': '3 years',
    '5_years': '5 years',
  }
  if (map[value]) return map[value]
  // Handle numeric values (assumed months)
  const num = parseInt(value, 10)
  if (!isNaN(num) && num > 0) {
    if (num === 1) return '1 month'
    if (num === 12) return '1 year'
    if (num === 24) return '2 years'
    if (num === 36) return '3 years'
    if (num === 60) return '5 years'
    if (num % 12 === 0) return `${num / 12} years`
    return `${num} months`
  }
  // Fallback: replace underscores with spaces
  return value.replace(/_/g, ' ')
}

const QualificationCard = memo(function QualificationCard({ qualification }) {
  const [expanded, setExpanded] = useState(false)

  const displayStatus = getDisplayStatus(qualification)
  const config = statusConfig[displayStatus] || statusConfig.pending
  const StatusIcon = config.icon

  const days = getDaysUntilExpiry(qualification.expiry_date)
  const expiryText = formatExpiryText(days)
  const hasEvidence = !!qualification.evidence_url || qualification.evidence_files?.length > 0
  const spaces = qualification.spaces || []
  const reqName = qualification.requirement_name || 'Unknown'
  const reqCategory = qualification.requirement_category
  const renewalLabel = formatRenewalPeriod(qualification.renewal_period)

  const handleViewEvidence = () => {
    const url = qualification.evidence_url
    if (!url) return
    const viewUrl = url.startsWith('http') ? url : gcsGetPublicUrl(url)
    window.open(viewUrl, '_blank')
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        className="w-full text-left p-4 active:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 text-sm">{reqName}</p>
            {reqCategory && (
              <p className="text-xs text-gray-400 mt-0.5">{reqCategory}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge variant="outline" className={cn('text-xs whitespace-nowrap', config.className)}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {config.label}
            </Badge>
            {expanded ? <ChevronUp className="h-4 w-4 text-gray-300" /> : <ChevronDown className="h-4 w-4 text-gray-300" />}
          </div>
        </div>

        {expiryText && (
          <p className={cn('text-xs mt-1.5', displayStatus === 'expired' ? 'text-red-600 font-medium' : displayStatus === 'expiring_soon' ? 'text-amber-600 font-medium' : 'text-gray-500')}>
            {expiryText}
          </p>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
          <div className="grid grid-cols-2 gap-3">
            {qualification.completed_date && (
              <div>
                <p className="text-xs text-gray-400">Completed</p>
                <p className="text-sm text-gray-700">
                  {new Date(qualification.completed_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            )}
            {qualification.expiry_date && (
              <div>
                <p className="text-xs text-gray-400">Expires</p>
                <p className={cn('text-sm', displayStatus === 'expired' ? 'text-red-600 font-medium' : displayStatus === 'expiring_soon' ? 'text-amber-600 font-medium' : 'text-gray-700')}>
                  {new Date(qualification.expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            )}
          </div>

          {qualification.certificate_number && (
            <div>
              <p className="text-xs text-gray-400">Certificate Number</p>
              <p className="text-sm text-gray-700">{qualification.certificate_number}</p>
            </div>
          )}

          {renewalLabel && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Calendar className="h-3 w-3" />
              Renews every {renewalLabel}
            </div>
          )}

          {spaces.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 mb-1">Assigned Spaces</p>
              <div className="flex items-center gap-1 flex-wrap">
                {spaces.map(s => (
                  <span key={s.id} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {qualification.notes && (
            <div>
              <p className="text-xs text-gray-400">Notes</p>
              <p className="text-sm text-gray-600">{qualification.notes}</p>
            </div>
          )}

          {hasEvidence && (
            <div className="pt-1">
              <Button variant="outline" size="sm" onClick={handleViewEvidence}>
                <Eye className="h-4 w-4 mr-1.5" />
                View Evidence
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
})

export default QualificationCard
